import { seedChecks } from '../lib/checks'
import { SEED_TRIP } from '../lib/seed'
import { KIND, SINGLETON_ID } from '../lib/types'
import { allDocs, putDocs, type LocalDoc } from './db'

/**
 * Die Beispielreise, und wie sie in eine frisch angelegte Reise kommt.
 *
 * ## Warum das hier steht und nicht mehr im Provider
 *
 * Bis hierher legte der RoadbookProvider die Vorlage in jede Reise, in der
 * kein Reisedokument lag. Solange ein Nutzer genau eine Reise hatte, war das
 * richtig: es gab keinen anderen Fall. Seit Reisen eigenständige Objekte sind,
 * ist es falsch - wer "Atlantik 2027" anlegt, bekam sechzehn Balkanetappen und
 * eine Vignettenliste von Slowenien bis Bulgarien.
 *
 * Die Vorlage ist trotzdem nichts, was weggehört. Sie zeigt an einer
 * ausgearbeiteten Reise, was die App kann - Etappen mit Koordinaten,
 * Übernachtungen, Hinweise, Vignetten, Ersatzteilkiste -, und das ist mit
 * einer leeren Reise vor sich nicht zu erraten. Sie bleibt also, aber hinter
 * einer Entscheidung: der Knopf "Mit Beispieldaten" neben dem gewöhnlichen
 * Anlegen. Zwei Knöpfe und kein Häkchen, weil es zwei verschiedene Dinge sind,
 * die man tut, und nicht eine Einstellung an einem Ding.
 *
 * ## Warum beim Anlegen und nicht beim ersten Öffnen
 *
 * Weil das Anlegen der Moment ist, in dem die Entscheidung fällt. Wird die
 * Vorlage stattdessen beim ersten Öffnen gelegt, muss der Wunsch von der
 * Auswahl bis in den Bestand transportiert werden - über einen Merker im
 * localStorage oder eine Sonderspalte -, und jeder Weg dorthin kann schief
 * gehen. Hier wird geschrieben, bevor die Reise überhaupt aufgeht.
 *
 * ## Die Markierung seed
 *
 * Sie bleibt, und sie ist hier genauso nötig wie vorher: seed 1 kennzeichnet
 * die Vorlage als das, was sie ist. Nur so darf der Abgleich sie bedingungslos
 * gegen ein Serverdokument mit Vorgeschichte verlieren lassen, ohne dabei
 * echte, offline entstandene Inhalte mitzunehmen (siehe mergeRemote in
 * merge.ts). Gebraucht wird das, sobald die frisch angelegte Reise auf einem
 * zweiten Gerät aufgeht, bevor der Push des ersten durch ist: dann liegt hier
 * eine unangetastete Vorlage und dort die echte Reise, und die Vorlage ist per
 * Konstruktion die jüngere.
 */

function key(kind: string, id: string): string {
  return `${kind}:${id}`
}

/**
 * Die Dokumente der Beispielreise.
 *
 * Rein und ohne Datenbank, damit prüfbar bleibt, was angelegt wird - vor allem
 * die Markierung seed und dass die Dokumente schmutzig sind, also zum Server
 * gehen.
 *
 * @param now Zeitstempel der Anlage.
 */
export function beispielDokumente(now: number): LocalDoc[] {
  return [
    {
      kind: KIND.trip,
      id: SINGLETON_ID.trip,
      body: SEED_TRIP,
      rev: 0,
      deleted: false,
      dirty: 1,
      updatedAt: now,
      seed: 1,
    },
    {
      kind: KIND.checks,
      id: SINGLETON_ID.checks,
      // Die recherchierten Vignetten und die Ersatzteilkiste als
      // Ausgangspunkt. Ergänzt und geändert wird das in der App.
      body: seedChecks(),
      rev: 0,
      deleted: false,
      dirty: 1,
      updatedAt: now,
      seed: 1,
    },
  ]
}

/**
 * Legt die Beispielreise in eine Reise, in der noch nichts steht.
 *
 * Geschrieben wird nur, was fehlt. Das ist kein Zierrat: beim Anlegen der
 * ersten Reise kann derselbe Handgriff den Altbestand dieses Geräts in die
 * neue Reise überführen (adoptiereAltbestand). Dessen Datenbank bringt ihre
 * eigenen Dokumente mit, und die Vorlage darüberzuschreiben hiesse, eine
 * fertig geplante Reise durch eine Vorlage zu ersetzen - mit dem Abgleich
 * hinterher, der das auf jedes andere Gerät trägt.
 *
 * @param now Zeitstempel der Anlage; als Parameter, damit ein Test ihn setzen
 *   kann.
 */
export async function legeBeispielAn(tripId: string, now = Date.now()): Promise<void> {
  const vorhanden = new Set((await allDocs(tripId)).map((doc) => key(doc.kind, doc.id)))
  const fehlend = beispielDokumente(now).filter((doc) => !vorhanden.has(key(doc.kind, doc.id)))
  await putDocs(tripId, fehlend)
}
